"use client"

import { getProductsByCategory } from "@/lib/api/dutchie";
import CategoryLayout, { CategoryConfig } from "@/components/shop/CategoryLayout";
import type { ProductDisplay } from "@/lib/api/dutchie";

/* ------------------------------------------------------------------
 🔍  CATEGORY CONSTANTS
-------------------------------------------------------------------*/
const CATEGORY = "topicals";
const EFFECTS = [
  "Relief",
  "Cooling",
  "Warming",
  "Soothing",
  "Anti-inflammatory",
  "Recovery",
  "Relaxing",
  "Therapeutic",
];
/** Regex → Virtual Sub‑category label */
const SUBCATEGORY_REGEX: [RegExp, string][] = [
  [/\b(balm|salve)\b/i, "Balms & Salves"],
  [/\b(lotion|cream|creme)\b/i, "Lotions & Creams"],
  [/\b(roll[- ]?on)\b/i, "Roll‑ons"],
  [/\b(patch|transdermal)\b/i, "Patches"],
  [/\b(spray|mist)\b/i, "Sprays"],
  [/\b(bath|soak|bomb)\b/i, "Bath Products"],
  [/\b(massage|oil)\b/i, "Massage Oils"],
  [/\b(gel|ointment)\b/i, "Gels"],
];

/* ------------------------------------------------------------------
 🧩  HELPERS
-------------------------------------------------------------------*/
// Derive virtual sub‑category
const categorizeTopical = (p: ProductDisplay): string => {
  const txt = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase();
  for (const [re, label] of SUBCATEGORY_REGEX) if (re.test(txt)) return label;
  return p.subcategory && p.subcategory !== "DEFAULT" ? p.subcategory : "Other";
};
// Pull effect keywords
const extractEffects = (p: ProductDisplay) => {
  const txt = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase();
  return EFFECTS.filter((e) => txt.includes(e.toLowerCase()));
};

/* ------------------------------------------------------------------
 📄  PAGE COMPONENT
-------------------------------------------------------------------*/
export default function TopicalsPage() {
  /* ---------------- data fetch ---------------- */
  const fetchProducts = async (): Promise<ProductDisplay[]> => {
    try {
      const raw = await getProductsByCategory(CATEGORY);
      if (!Array.isArray(raw)) return [];
      // de‑dupe by id
      const unique = Object.values(
        raw.reduce<Record<string, ProductDisplay>>((m, p) => {
          if (p.id) m[p.id] = p;
          return m;
        }, {})
      );
      // enrich with virtual props
      return unique.map((p) => ({
        ...p,
        virtualSubcategory: categorizeTopical(p),
        effects: extractEffects(p),
      }));
    } catch (err) {
      console.error("❌ Failed to fetch topicals:", err);
      return [];
    }
  };

  /* --------------- layout config -------------- */
  const config: CategoryConfig = {
    title: "Topicals",
    blurb: "Discover soothing cannabis‑infused lotions, balms, patches, and more for targeted relief.",
    fetch: fetchProducts,
    virtualSubcategory: categorizeTopical,
    effects: EFFECTS,
    potencyUnit: "%",
    showPotency: true,
    potencyFilterMode: "both",
    filterInStock: false,
    headerTheme: "green", // teal‑ish vibe
    showWeight: false,
    extraFilterChips: (p) => ({ Effects: extractEffects(p) }),
  };

  /* ------------------- UI --------------------- */
  return <CategoryLayout config={config} productCardProps={{ buttonClassName: "bg-green-600 hover:bg-green-700" }} />;
}
